import { apiRequest } from '../../../services/api/client';
import { ClassStudentRecord } from '../mocks/classStudents';

type StudentSegmentResource = {
  id: number;
  name: string;
  minimum_age: number | null;
  maximum_age: number | null;
};

type StudentResource = {
  id: number;
  class_id: number;
  member_id: number | null;
  full_name: string;
  birth_date: string | null;
  phone: string | null;
  responsible_name: string | null;
  active: boolean;
  enrolled_at: string | null;
  notes: string | null;
  class?: {
    id: number;
    name: string;
    segment?: StudentSegmentResource | null;
  } | null;
};

type MemberCandidateResource = {
  id: number;
  full_name: string;
  birth_date: string | null;
  phone: string | null;
  gender: string | null;
  ecclesiastical_role?: {
    id: number;
    name: string;
  } | null;
  active_student?: {
    id: number;
    class_id: number;
    class_name: string | null;
  } | null;
};

type StudentListResponse = {
  data: StudentResource[];
};

type StudentResponse = {
  data: StudentResource;
};

type MemberCandidatesResponse = {
  data: MemberCandidateResource[];
};

export interface MemberCandidateRecord {
  id: string;
  name: string;
  birthDate?: string;
  phone?: string;
  gender?: string;
  role?: string;
  activeStudent?: {
    id: string;
    classId: string;
    className: string;
  } | null;
}

export interface SaveClassStudentInput {
  memberId?: string | null;
  fullName?: string;
  birthDate?: string;
  phone?: string;
  responsibleName?: string;
  enrolledAt?: string;
  notes?: string;
}

const normalizeAgeRange = (segment?: StudentSegmentResource | null) => {
  if (!segment) return 'Não informado';
  if (segment.minimum_age === null && segment.maximum_age === null) return 'Não informado';
  if (segment.minimum_age !== null && segment.maximum_age !== null) {
    return `${segment.minimum_age} a ${segment.maximum_age} anos`;
  }
  if (segment.minimum_age !== null) return `${segment.minimum_age}+`;
  return `Ate ${segment.maximum_age} anos`;
};

const toStudent = (student: StudentResource): ClassStudentRecord => {
  const classId = String(student.class_id);
  const className = student.class?.name ?? 'Turma';

  return {
    id: String(student.id),
    name: student.full_name,
    ageGroup: student.class?.segment?.name ?? 'Sem segmento',
    ageLabel: normalizeAgeRange(student.class?.segment),
    birthDate: student.birth_date ?? undefined,
    joinedEbdAt: student.enrolled_at ?? '',
    notes: student.notes ?? '',
    attendanceRate: 0,
    consecutiveAbsences: 0,
    currentClassId: student.active ? classId : null,
    classHistory: [
      {
        classId,
        className,
        enteredAt: student.enrolled_at ?? '',
      },
    ],
  };
};

const toMemberCandidate = (member: MemberCandidateResource): MemberCandidateRecord => ({
  id: String(member.id),
  name: member.full_name,
  birthDate: member.birth_date ?? undefined,
  phone: member.phone ?? undefined,
  gender: member.gender ?? undefined,
  role: member.ecclesiastical_role?.name,
  activeStudent: member.active_student
    ? {
        id: String(member.active_student.id),
        classId: String(member.active_student.class_id),
        className: member.active_student.class_name ?? 'Turma ativa',
      }
    : null,
});

const toPayload = (input: SaveClassStudentInput) => ({
  member_id: input.memberId ? Number(input.memberId) : undefined,
  full_name: input.fullName?.trim() || undefined,
  birth_date: input.birthDate || null,
  phone: input.phone?.trim() || null,
  responsible_name: input.responsibleName?.trim() || null,
  enrolled_at: input.enrolledAt || null,
  notes: input.notes?.trim() || null,
});

export const classStudentService = {
  async listByClass(classId: string, trimesterId?: string): Promise<ClassStudentRecord[]> {
    const params = new URLSearchParams({ per_page: '200' });

    if (trimesterId) {
      params.set('quarter_id', trimesterId);
    }

    const payload = await apiRequest<StudentListResponse>(`/ebd/classes/${classId}/students?${params.toString()}`);
    return payload.data.map(toStudent);
  },

  async memberCandidates(classId: string): Promise<MemberCandidateRecord[]> {
    const payload = await apiRequest<MemberCandidatesResponse>(`/ebd/students/member-candidates?class_id=${classId}&limit=50`);
    return payload.data.map(toMemberCandidate);
  },

  async create(classId: string, input: SaveClassStudentInput): Promise<ClassStudentRecord> {
    const payload = await apiRequest<StudentResponse>(`/ebd/classes/${classId}/students`, {
      method: 'POST',
      body: JSON.stringify(toPayload(input)),
    });

    return toStudent(payload.data);
  },

  async update(studentId: string, input: SaveClassStudentInput): Promise<ClassStudentRecord> {
    const payload = await apiRequest<StudentResponse>(`/ebd/students/${studentId}`, {
      method: 'PATCH',
      body: JSON.stringify(toPayload(input)),
    });

    return toStudent(payload.data);
  },

  async transfer(studentId: string, targetClassId: string): Promise<ClassStudentRecord> {
    const payload = await apiRequest<StudentResponse>(`/ebd/students/${studentId}/transfer`, {
      method: 'PATCH',
      body: JSON.stringify({ target_class_id: Number(targetClassId) }),
    });

    return toStudent(payload.data);
  },

  async remove(studentId: string): Promise<void> {
    await apiRequest<void>(`/ebd/students/${studentId}`, {
      method: 'DELETE',
    });
  },
};
